/**
 * @file CourseList.jsx
 * @author Rishikesh
 * @date 2024-09-11
 * @description Show the list of courses
 */
import React from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  InputAdornment,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
} from "@mui/material";
import { ContentPasteSearchOutlined, Visibility } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

export const CourseList = ({ isLoading, courses }) => {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = React.useState("");

  // Filter courses based on search query
  const filteredUsers = courses.filter((course) =>
    course.coursename.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <Stack
        direction={"row"}
        alignItems={"end"}
        justifyContent="space-between"
        pt={1}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          columnGap={2}
          rowGap={2}
        >
          {isLoading ? (
            <Skeleton
              sx={{ mt: 3 }}
              variant="rounded"
              width={390}
              height={24}
            />
          ) : (
            <Typography
              sx={{ mt: 2, mb: 0.5 }}
              fontWeight={700}
              fontSize={24}
              color="#222222"
            >
              Courses
            </Typography>
          )}
          {!isLoading && (
            <TextField
              id="outlined-start-adornment"
              placeholder="Search courses"
              className="inputRounded"
              color="black"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <ContentPasteSearchOutlined />
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        </Stack>
        {isLoading ? (
          <Skeleton variant="rounded" width={180} height={40} />
        ) : (
          <Button
            variant="contained"
            sx={{ borderRadius: "12px" }}
            onClick={() =>
              navigate(`/master-settings/courses/manage-course/new`)
            }
          >
            Add Course
          </Button>
        )}
      </Stack>
      <Box
        sx={{
          width: "100%",
          overflowX: "auto",
          "&::-webkit-scrollbar": { display: "none" },
          pb: 1,
        }}
      >
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 2 }}
            width="100%"
            height={400}
          />
        ) : (
          <TableContainer sx={{ mt: 2 }}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: "#3A80F7",
                  }}
                >
                  <TableCell sx={{ color: "white", borderTopLeftRadius: 8 }}>
                    Name
                  </TableCell>
                  <TableCell align="center" sx={{ color: "white" }}>
                    Categories
                  </TableCell>
                  <TableCell align="center" sx={{ color: "white" }}>
                    Status
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: "white", borderTopRightRadius: 8 }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((course) => (
                    <TableRow key={course.id}>
                      <TableCell
                        sx={{
                          minWidth: 150,
                          opacity: course.isactive === 1 ? 1 : 0.4,
                        }}
                      >
                        {course.coursename}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          minWidth: 150,
                          opacity: course.isactive === 1 ? 1 : 0.4,
                        }}
                      >
                        {course.category}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          minWidth: 150,
                          opacity: course.isactive === 1 ? 1 : 0.4,
                        }}
                      >
                        {course.isactive === 1 ? "Active" : "Inactive"}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() =>
                            navigate(
                              `/master-settings/courses/manage-course/${course.id}`,
                              {
                                state: { course: course },
                              }
                            )
                          }
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No courses found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </>
  );
};
